import React from 'react'
import {
  AssignmentOutlined,
  CalendarMonthOutlined,
  PrecisionManufacturingOutlined,
  SpeedOutlined,
  SwapHorizOutlined,
  TextSnippetOutlined,
  WarningAmberOutlined
} from '@mui/icons-material'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { bucketRailClass, statusDisplay, formatClock } from './byGroupVisuals'
import { systemValueCodeLabel, systemValueLabel } from '../generic/systemValues/systemValueLabel'
import DeviceHeroImage from './DeviceHeroImage'
import PreparationLabel from '../Mos/Widgets/common/PreparationLabel'
import MachineResourcesList from './MachineResourcesList'
import MachineAlarmsPanel from './MachineAlarmsPanel'
import AssetResourcesButton from './AssetResourcesButton'
import AssetUnlockButton from './AssetUnlockButton'

const EM_DASH = '—'

function TouchAction ({ testId, icon: Icon, label, disabled, onClick }) {
  return (
    <button type='button' data-testid={testId} className='mcx-touch-btn mcx-touch-btn--lg' disabled={disabled} onClick={onClick}>
      <Icon sx={{ fontSize: 32 }} />
      <span className='mcx-touch-btn__label'>{label}</span>
    </button>
  )
}

/** One label/value field row inside the ORDER / WORK panels. */
function Field ({ label, value, testId, mono = true }) {
  return (
    <div className='mcx-field' data-testid={testId}>
      <span className='mcx-field__label'>{label}</span>
      <span className={`mcx-field__value${mono ? ' mcx-field__value--mono' : ''}${value ? '' : ' mcx-field__value--empty'}`}>
        {value || EM_DASH}
      </span>
    </div>
  )
}

/**
 * Generic driver telemetry: `driverInfo` is a FREE `Map<String,String>` — sun does not
 * fix the keys, each machine driver fills its own (spindle load, RPM, feed override, NC
 * block, part counter, cycle time, coolant, axes, gripper…). So render every entry as a
 * key/value row without knowing the keys in advance. Renders nothing when the map is empty.
 */
function DriverInfo ({ info, testId }) {
  const entries = Object.entries(info || {})
  if (entries.length === 0) return null
  return (
    <div className='mcx-driverinfo' data-testid={testId}>
      {entries.map(([key, value]) => (
        <div key={key} className='mcx-field'>
          <span className='mcx-field__label'>{key}</span>
          <span className='mcx-field__value mcx-field__value--mono'>{String(value ?? EM_DASH)}</span>
        </div>
      ))}
    </div>
  )
}

/** One live logistics movement involving the device (RECEIVING / SENDING / HANDLING). */
function MoveRow ({ move, index, t_ }) {
  const route = [move.fromDeviceName, move.toDeviceName].filter(Boolean).join(' → ')
  return (
    <div className='mcx-move' data-testid={`asset-detail-move-${index}`}>
      <div className='mcx-move__head'>
        <span className='mcx-chip mcx-chip--accent'>{systemValueCodeLabel(t_, 'RunningMove.Direction', move.direction) || EM_DASH}</span>
        {move.fixtureCode && <span className='mcx-move__fixture'>{move.fixtureCode}</span>}
        {move.startedAtMillis ? <span className='mcx-move__time'>{formatClock(move.startedAtMillis)}</span> : null}
      </div>
      {route && <div className='mcx-move__route'>{route}</div>}
      {move.handlerName && (
        <div className='mcx-field'>
          <span className='mcx-field__label'>{t_('Handler')}</span>
          <span className='mcx-field__value mcx-field__value--mono'>{move.handlerName}</span>
        </div>
      )}
      <DriverInfo info={move.driverInfo} />
    </div>
  )
}

/**
 * AssetDetailView — the PRODUCTION fullscreen detail, redesigned (story 35) to consume
 * the real `monitoring/deviceProduction/<id>` endpoint (via `production`), transcribed
 * from the approved Stitch screen "Production Asset Detail — Order & Work":
 *
 *   header (thumbnail + name + type + MODE + status pill + unlock)
 *   → REAL metric row (ELAPSED since task start / THEORETICAL task time / PLANNED qty)
 *   → ORDER panel (fabrication order, part reference, technology + job chips)
 *   → WORK panel (program / task / active part + part-status chip / job & task start
 *     times / operation result)
 *   → DRIVER TELEMETRY — the free-form `driverInfo` map rendered as key/value rows
 *   → MOVEMENTS — live logistics moves (RECEIVING/SENDING/HANDLING) involving the device
 *   → ERROR banner ONLY for real faults (OPEN_DOOR/CLOSE_DOOR/CHECK_CLAMP) or a FAIL
 *     result — never the normal WORK state
 *   → PARTS ON MACHINE list (from `locations`, i.e. fixtureLocations — a separate
 *     concern) → incidence badge → touch action bar (Resources + Documents + Calendar).
 *
 * `order`/`work` are `{}` when sun omits them (no active OF / no operation in progress):
 * each block then renders its empty state. Presentation only; `AssetDetail.jsx` wires
 * the data + `AssetCardDialogs` actions/permissions. Close is handled by SmartDialog.
 */
export default function AssetDetailView ({ device, actions, locations = [], production = {}, alarms = [], mainImageInfo = {} }) {
  const { t_ } = useSmartTranslation()
  // Display-only: shows sun's bucket when present, otherwise the device's REAL raw
  // status (PO request) — filtering/KPIs keep using getDeviceBucket (RN3).
  const display = statusDisplay(device)
  const bucket = display.bucket
  const pulsing = bucket === 'WORKING' || bucket === 'ERROR'
  const incidences = device.incidenceQuantity ?? 0

  const order = production?.order ?? {}
  const work = production?.work ?? {}
  const moves = production?.moves ?? []
  const driver = work?.driverInfo ?? {}
  const hasOrder = Object.keys(order).length > 0
  const hasWork = Object.keys(work).length > 0

  const partReference = [order.partReferenceCode, order.partReferenceName].filter(Boolean).join(' · ')
  // errorState can be WORK (the NORMAL in-progress state) or a real fault; only the
  // fault values raise the red banner — WORK is not an error.
  const errorState = work?.errorState ?? ''
  const operationResult = work?.operationResult ?? ''
  const isFault = ['OPEN_DOOR', 'CLOSE_DOOR', 'CHECK_CLAMP'].includes(errorState)
  const showErrorBanner = isFault || operationResult === 'FAIL'

  return (
    <div className='mcx-root mcx-detail' data-testid='asset-detail' data-bucket={bucket}>
      {/* Header */}
      <div className='mcx-detail__head'>
        <span className='mcx-detail__thumb'><DeviceHeroImage device={device} mainImageInfo={mainImageInfo} /></span>
        <div className='mcx-detail__idblock'>
          <h2 className='mcx-detail__name'>{device.name}</h2>
          {device?.deviceType?.name && <span className='mcx-detail__type'>{systemValueLabel(t_, 'DeviceTypeEnum', device.deviceType.name)}</span>}
        </div>
        <span className='mcx-detail__statusgroup'>
          <div className='mcx-detail__mode'>
            <span className='mcx-eyebrow'>{t_('Mode')}</span>
            <span data-testid='asset-detail-mode' className='mcx-detail__mode-value'>{device.mode ? systemValueLabel(t_, 'DeviceModeEnum', device.mode) : EM_DASH}</span>
          </div>
          <span
            data-testid='asset-detail-status'
            className={`mcx-status-pill mcx-status-pill--${bucket}${pulsing ? ' mcx-pulse' : ''}`}
          >
            <span className='mcx-dot' style={{ width: 8, height: 8, background: 'currentColor' }} />
            {display.statusCode === '' ? EM_DASH : systemValueLabel(t_, display.statusDomain, display.statusCode)}
          </span>
          <AssetUnlockButton device={device} />
        </span>
      </div>

      {/* ORDER + WORK panels */}
      <div className={`mcx-detail__grid ${bucketRailClass(bucket)}`}>
        {/* ORDER */}
        <div className='mcx-data-panel' data-testid='asset-detail-order'>
          <div className='mcx-data-panel__head'>
            <AssignmentOutlined sx={{ fontSize: 20 }} />
            <span className='mcx-label-caps'>{t_('Fabrication order')}</span>
          </div>
          {hasOrder
            ? (
              <>
                {/* S153 (A1/C7): the job block's mark comes in the order block of
                    `monitoring/deviceProduction`. Same label component as everywhere else. */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', minWidth: 0 }}>
                  <p className='mcx-data-panel__value' data-testid='asset-detail-order-code'>{order.fabricationOrderCode || EM_DASH}</p>
                  <PreparationLabel visible={order?.jobInPreparation === true} />
                </div>
                <Field label={t_('Part reference')} value={partReference} testId='asset-detail-part-reference' />
                <div className='mcx-chiprow'>
                  {order.technology && <span className='mcx-chip mcx-chip--accent'>{order.technology}</span>}
                  {order.jobName && <span className='mcx-chip'>{order.jobName}</span>}
                </div>
              </>
              )
            : <p className='mcx-data-panel__value mcx-data-panel__value--empty' data-testid='asset-detail-order-empty'>{t_('No active fabrication order')}</p>}
        </div>

        {/* WORK */}
        <div className='mcx-data-panel' data-testid='asset-detail-work'>
          <div className='mcx-data-panel__head'>
            <PrecisionManufacturingOutlined sx={{ fontSize: 20 }} />
            <span className='mcx-label-caps'>{t_('Work')}</span>
          </div>
          {hasWork
            ? (
              <>
                <Field label={t_('Program')} value={work.programName} testId='asset-detail-program' />
                <Field label={t_('Task')} value={work.taskName} testId='asset-detail-task' />
                <div className='mcx-field'>
                  <span className='mcx-field__label'>{t_('Active part')}</span>
                  <span className='mcx-field__value mcx-field__value--mono'>
                    {work.partCode || EM_DASH}
                    {work.partStatus && <span className='mcx-chip mcx-chip--sm'>{systemValueCodeLabel(t_, 'PartStatusEnum', work.partStatus)}</span>}
                  </span>
                </div>
                <div className='mcx-timerow'>
                  <div className='mcx-field'>
                    <span className='mcx-field__label'>{t_('Started')}</span>
                    <span className='mcx-field__value mcx-field__value--mono'>{formatClock(work.jobStartedAtMillis) || EM_DASH}</span>
                  </div>
                  <div className='mcx-field'>
                    <span className='mcx-field__label'>{t_('Task started')}</span>
                    <span className='mcx-field__value mcx-field__value--mono'>{formatClock(work.taskStartedAtMillis) || EM_DASH}</span>
                  </div>
                </div>
                {operationResult && operationResult !== 'FAIL' && (
                  <Field label={t_('Result')} value={systemValueCodeLabel(t_, 'OperationResultEnum', operationResult)} testId='asset-detail-result' />
                )}
              </>
              )
            : <p className='mcx-data-panel__value mcx-data-panel__value--empty' data-testid='asset-detail-work-empty'>{t_('No operation in progress')}</p>}
        </div>
      </div>

      {/* Machine alarms (contract §10.2): read-only panel, on-demand from
          monitoring/deviceAlarms/<id>. Always shown (empty state when none) so the
          detail confirms "No active alarms" as well as surfacing active ones. */}
      <MachineAlarmsPanel alarms={alarms} />

      {/* Driver telemetry — free-form key/value map filled by each machine driver */}
      {hasWork && Object.keys(driver).length > 0 && (
        <div className='mcx-teltrip' data-testid='asset-detail-telemetry'>
          <div className='mcx-teltrip__head'>
            <SpeedOutlined sx={{ fontSize: 18 }} />
            <span className='mcx-label-caps mcx-label-caps--xs'>{t_('Driver')}</span>
          </div>
          <DriverInfo info={driver} testId='asset-detail-driver' />
        </div>
      )}

      {/* Live logistics movements involving this device (RECEIVING/SENDING/HANDLING) */}
      {moves.length > 0 && (
        <div className='mcx-teltrip' data-testid='asset-detail-moves'>
          <div className='mcx-teltrip__head'>
            <SwapHorizOutlined sx={{ fontSize: 18 }} />
            <span className='mcx-label-caps mcx-label-caps--xs'>{t_('Movements')} ({moves.length})</span>
          </div>
          <div className='mcx-moves'>
            {moves.map((move, index) => <MoveRow key={index} move={move} index={index} t_={t_} />)}
          </div>
        </div>
      )}

      {/* Error banner: only REAL faults (OPEN_DOOR/CLOSE_DOOR/CHECK_CLAMP) or a FAIL
          result — never the normal WORK state. */}
      {showErrorBanner && (
        <div className='mcx-errorbanner' data-testid='asset-detail-error'>
          <WarningAmberOutlined sx={{ fontSize: 20 }} />
          {isFault && <span className='mcx-errorbanner__state'>{systemValueCodeLabel(t_, 'CellWorkErrorEnum', errorState)}</span>}
          {operationResult && <span className='mcx-errorbanner__result'>{t_('Result')}: {systemValueCodeLabel(t_, 'OperationResultEnum', operationResult)}</span>}
        </div>
      )}

      {/* Resources on machine — frontend-paginated (a storage can hold 100+) */}
      <MachineResourcesList locations={locations} testIdPrefix='asset-detail-part-line' pageSize={8} style={{ padding: '0 24px 12px' }} />

      {incidences > 0 && (
        <div style={{ padding: '0 24px 12px' }}>
          <span className='mcx-incidence-badge' data-testid='asset-detail-incidence'>
            {incidences} {incidences === 1 ? t_('Incidence') : t_('Incidences')}
          </span>
        </div>
      )}

      {/* Big touch action bar: unified Resources + Documents + Calendar */}
      <div className='mcx-detail__actions'>
        <AssetResourcesButton actions={actions} deviceId={device.id} large hasResources={locations.length > 0} />
        <TouchAction
          testId='asset-detail-action-documents'
          icon={TextSnippetOutlined}
          label={t_('Documents')}
          disabled={!actions.isUserAuthorized('DOCUMENTS_DEVICE')}
          onClick={() => actions.openDocument()}
        />
        <TouchAction
          testId='asset-detail-action-calendar'
          icon={CalendarMonthOutlined}
          label={t_('Calendar')}
          disabled={!actions.isUserAuthorized('CALENDARS')}
          onClick={() => actions.goToCalendar()}
        />
      </div>
    </div>
  )
}
